import { useTranslation } from "react-i18next";
import { ButtonVariant } from "../../types/constants";
import Button from "../ui/Button";
import CourseCard from "../CourseCard";
import { courseServiceFactory } from "../../services/CourseServiceImpl";
import { useInfiniteQuery } from "@tanstack/react-query";
import LoadingPanel from "../../custom-components/CustomLoader";
import { useMemo } from "react";
import { newsPageSize } from "../../Config";
import { ICourseFlat } from "../../domain/models/Course";

const courseService = courseServiceFactory();
const COURSES_PAGE_SIZE = newsPageSize;

interface CoursesListSectionProps {
    selectedTopics?: string[];
}

export default function CoursesListSection({ selectedTopics = [] }: CoursesListSectionProps) {
    const { t, i18n } = useTranslation();
    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } = useInfiniteQuery({
        queryKey: ["courses", "list", i18n.language, selectedTopics],
        queryFn: async ({ pageParam = 1 }) => {
            const result = await courseService.getAllCourses(pageParam, COURSES_PAGE_SIZE);
            return result;
        },
        getNextPageParam: (lastPage, allPages) => {
            if (lastPage.totalPages > allPages.length) {
                return allPages.length + 1;
            }
            return undefined;
        },
        initialPageParam: 1,
    });

    const allCourses = useMemo<ICourseFlat[]>(() => {
        return data?.pages.flatMap((page) => page.rows) ?? [];
    }, [data]);

    const handleLoadMore = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };

    if (isLoading) {
        return <LoadingPanel isVisible={isLoading} />;
    }

    if (isError || allCourses.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <p className="text-text-300 text-lg mb-4">{t("courses.noCourses")}</p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-[1200px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {allCourses.map((course) => (
                    <CourseCard key={course.id} course={course} showButton={true} showDetails={true} />
                ))}
            </div>

            {hasNextPage && (
                <div className="flex justify-center mt-8">
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        onClick={handleLoadMore}
                        disabled={isFetchingNextPage}
                        text={isFetchingNextPage ? t("common.loading") : t("common.loadMore")}
                    />
                </div>
            )}
        </div>
    );
}
